import { createContext, useState, RefObject, useEffect } from 'react';
import { trackQuestionSkip } from '../question/question.tracking';
import { AnswerData, QuestionOption } from '../question/question.types';

type Option = { value: string; name: string };

export type QuestionSteps = {
    question: string;
    value: string | undefined | null;
};

export type QuestionStep = {
    label: string;
    options: QuestionOption[];
    subtitle?: string;
    getNext: (value: string, situationBranch: string) => string | 'done';
    formatAnswer: (answer: { value: string; label: string }) => string;
};

export type Props = {
    initialQuestionId: string;
    showAllQuestions?: boolean;
    analyticsPage: string;
    onSubmit: (data: AnswerData) => Promise<void>;
    onSkip?: () => void;
    initialValues?: Record<string, string | null | undefined>;
    questionsMap: Record<string, QuestionStep>;
    ref?: {
        latestQuestionRef?: RefObject<HTMLElement>;
        successMessageRef?: RefObject<HTMLElement>;
    };
    shouldFocusFirstQuestionOnMount?: boolean;
};

type QuestionKey = keyof Props['questionsMap'];

export const QuestionContext = createContext<{
    answers: QuestionSteps[];
    lastQuestionId: string;
} | null>(null);

export const useQuestion = ({
    initialQuestionId,
    questionsMap,
    initialValues,
    analyticsPage,
    onSkip,
    onSubmit,
    ref,
    shouldFocusFirstQuestionOnMount
}: Props) => {
    const DEFAULT_QUESTION = [
        {
            question: initialQuestionId,
            value: undefined
        }
    ];

    const getInitialSteps = (
        initialValues?: Props['initialValues']
    ): QuestionSteps[] => {
        if (!initialValues) {
            return DEFAULT_QUESTION;
        }

        const startArray: QuestionSteps[] = [];
        const questionsOrderedMap = Object.keys(initialValues);

        questionsOrderedMap.forEach((currentQuestion) => {
            const currentValue = initialValues?.[currentQuestion];
            if (!currentValue) {
                return;
            }
            startArray.push({ question: currentQuestion, value: currentValue });
        });

        if (startArray?.length === 0) {
            return DEFAULT_QUESTION;
        }

        return startArray;
    };

    const getInitialValue = (value: string, options: QuestionOption[]) => {
        return options.find((option) => option.value === value);
    };

    const [renderedSteps, setRenderedSteps] = useState<QuestionSteps[]>(
        initialValues ? getInitialSteps(initialValues) : DEFAULT_QUESTION
    );
    const [lastQuestionId, setLastQuestionId] = useState(initialQuestionId);
    const isFirstQuestionFilled = renderedSteps.length > 1;
    const checkIsOptionNameValid = (name: string): name is QuestionKey => {
        return name in questionsMap;
    };

    const onSelectedAnswer = (selectedOption: Option) => {
        const { name } = selectedOption;
        const getStepsOnSave = (
            currentSteps: QuestionSteps[],
            savedStep: Option
        ) => {
            const lastShownQuestion = currentSteps[currentSteps.length - 1];

            const arrayUntilChangedElement = currentSteps.slice(
                0,
                currentSteps.length - 1
            );

            if (lastShownQuestion.question === 'done') {
                return currentSteps;
            }

            const nextQuestion = questionsMap[
                lastShownQuestion?.question
            ].getNext(savedStep.value, currentSteps[0].value || '');

            return [
                ...arrayUntilChangedElement,
                // add saved step value
                {
                    ...lastShownQuestion,
                    value: savedStep.value
                },
                // get next step
                {
                    question: nextQuestion,
                    value: initialValues?.[nextQuestion]
                }
            ];
        };

        if (!checkIsOptionNameValid(name)) {
            throw new Error('Option is not valid.');
        }

        setRenderedSteps((previousState: QuestionSteps[]) => {
            return getStepsOnSave(previousState, selectedOption);
        });

        setTimeout(() => {
            ref?.latestQuestionRef?.current?.focus();
        }, 200);
    };

    const onEditAnswer = (optionName: string) => {
        if (!checkIsOptionNameValid(optionName)) {
            throw new Error('Option is not valid.');
        }

        const getStepsOnEdit = (
            currentSteps: QuestionSteps[],
            editingStep: QuestionKey
        ) => {
            const editingQuestionIndex = currentSteps.findIndex(
                (step) => step.question === editingStep
            );

            return currentSteps.filter((_, idx) => idx <= editingQuestionIndex);
        };

        setRenderedSteps((previousState) =>
            getStepsOnEdit(previousState, optionName)
        );

        setTimeout(() => {
            ref?.latestQuestionRef?.current?.focus();
        }, 200);
    };

    const onSubmitAnswer = async (data: AnswerData, questionId: string) => {
        await onSubmit(data);
        setLastQuestionId(questionId);
    };

    const onSkipButtonClicked = () => {
        trackQuestionSkip(lastQuestionId, analyticsPage);
        onSkip?.();
    };

    useEffect(() => {
        if (renderedSteps.some((step) => step.question === 'done')) {
            setTimeout(() => {
                ref?.successMessageRef?.current?.focus();
            }, 300);
        }
    }, [renderedSteps]);

    useEffect(() => {
        if (shouldFocusFirstQuestionOnMount) {
            ref?.latestQuestionRef?.current?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        renderedSteps,
        isFirstQuestionFilled,
        lastQuestionId,
        onSkipButtonClicked,
        onSubmitAnswer,
        onSelectedAnswer,
        onEditAnswer,
        getInitialValue
    };
};
